import {TsAudioSelector} from './tsAudioTracks';

export function createMpegAudioLoader(BaseLoader, audio, isCurrent) {
  const preferred = audio.preferred;
  return class AudioLoader extends BaseLoader {
    constructor(seekHandler, config) {
      super('audio-fetch-loader');
      this.seekHandler = seekHandler;
      this.config = config;
      this._needStash = true;
      this.controller = null;
    }

    open(source, range) {
      this.abort();
      const controller = new AbortController();
      this.controller = controller;
      this._status = 1;
      const filter = new TsAudioSelector(
        () => preferred,
        (tracks, selected) => {
          if (isCurrent()) audio.publish(tracks, selected);
        }
      );
      const request = this.seekHandler.getConfig(source.url, range);
      let received = 0;
      let outputLength = 0;
      const active = () => !controller.signal.aborted && isCurrent();
      const deliver = (bytes) => {
        if (!bytes.length || !active()) return;
        const start = range.from + outputLength;
        outputLength += bytes.length;
        this._onDataArrival?.(bytes.buffer, start, outputLength);
      };
      const run = async () => {
        const response = await fetch(request.url, {
          headers: {...request.headers, ...this.config.headers},
          credentials: source.withCredentials ? 'include' : 'same-origin',
          mode: source.cors === false ? 'same-origin' : 'cors',
          referrerPolicy: source.referrerPolicy || 'no-referrer',
          signal: controller.signal
        });
        if (!active()) {
          await response.body?.cancel();
          return;
        }
        if (!response.ok) {
          this._status = 3;
          this._onError?.('HttpStatusCodeInvalid', {
            code: response.status,
            msg: response.statusText
          });
          await response.body?.cancel();
          return;
        }
        const length = Number(response.headers.get('Content-Length')) || 0;
        if (length) this._onContentLengthKnown?.(length);
        this._status = 2;
        const reader = response.body.getReader();
        try {
          while (active()) {
            const {done, value} = await reader.read();
            if (!active()) return;
            if (done) break;
            received += value.byteLength;
            deliver(filter.push(value));
          }
          if (!active()) return;
          deliver(filter.push(new Uint8Array(), true));
          if (length && received < length) {
            this._status = 3;
            this._onError?.('EarlyEof', {code: -1, msg: 'Incomplete stream'});
          } else {
            this._status = 4;
            this._onComplete?.(range.from, range.from + outputLength - 1);
          }
        } finally {
          reader.releaseLock();
        }
      };
      run().catch(() => {
        if (!active()) return;
        this._status = 3;
        this._onError?.('Exception', {code: -1, msg: 'Stream unavailable'});
      });
    }

    abort() {
      this.controller?.abort();
      this.controller = null;
      this._status = 0;
    }

    destroy() {
      this.abort();
      super.destroy();
    }
  };
}

export function createHlsAudioLoader(BaseLoader, audio, isCurrent) {
  const preferred = audio.preferred;
  return class AudioFragmentLoader extends BaseLoader {
    load(context, config, callbacks) {
      // Process complete decrypted/plain TS fragments. fMP4 passes through;
      // encrypted fragments belong to Hls's decryption pipeline unchanged.
      super.load(context, config, {
        ...callbacks,
        onSuccess: (response, stats, ctx, details) => {
          if (!isCurrent()) return;
          if (response.data instanceof ArrayBuffer && !context.frag?.decryptdata?.key) {
            const filter = new TsAudioSelector(
              () => preferred,
              (tracks, selected) => {
                if (isCurrent()) audio.publish(tracks, selected);
              }
            );
            response.data = filter.push(response.data, true).buffer;
          }
          callbacks.onSuccess(response, stats, ctx, details);
        }
      });
    }
  };
}
